export type BrowserLanguageMatch = {
  languageCode: string;
  browserLanguage: string;
};

type ResolveBrowserLanguageInput = {
  browserLanguages?: readonly string[] | null;
  supportedLanguageCodes: readonly string[];
  preferredLanguageCodesByBase?: Readonly<Record<string, string>>;
  preferredLanguageCodesByLocale?: Readonly<Record<string, string>>;
};

const BROWSER_LANGUAGE_BASE_ALIASES: Readonly<Record<string, string>> = {
  no: "nb",
  tl: "fil",
  zh: "cmn",
};

function normalizeLanguageTag(value: string): string {
  return value.trim().replace(/_/g, "-").toLowerCase();
}

function getLanguageBase(value: string): string {
  return normalizeLanguageTag(value).split("-")[0] ?? "";
}

/**
 * Matches browser language preferences to the configured language catalog.
 * Exact locale matches win, followed by a deterministic base-language match.
 */
export function resolveBrowserLanguageCode({
  browserLanguages,
  preferredLanguageCodesByBase = {},
  preferredLanguageCodesByLocale = {},
  supportedLanguageCodes,
}: ResolveBrowserLanguageInput): BrowserLanguageMatch | null {
  if (!browserLanguages?.length || supportedLanguageCodes.length === 0) {
    return null;
  }

  const canonicalByNormalizedCode = new Map(
    supportedLanguageCodes.map((code) => [normalizeLanguageTag(code), code])
  );

  for (const browserLanguage of browserLanguages) {
    if (!browserLanguage?.trim()) continue;

    const exactMatch = canonicalByNormalizedCode.get(normalizeLanguageTag(browserLanguage));
    if (exactMatch) {
      return { browserLanguage, languageCode: exactMatch };
    }

    const preferredLocaleCode =
      preferredLanguageCodesByLocale[normalizeLanguageTag(browserLanguage)];
    if (preferredLocaleCode) {
      const canonicalPreferredLocaleCode = canonicalByNormalizedCode.get(
        normalizeLanguageTag(preferredLocaleCode)
      );
      if (canonicalPreferredLocaleCode) {
        return { browserLanguage, languageCode: canonicalPreferredLocaleCode };
      }
    }

    const rawBase = getLanguageBase(browserLanguage);
    const aliasedBase = BROWSER_LANGUAGE_BASE_ALIASES[rawBase] ?? rawBase;
    const preferredCode = preferredLanguageCodesByBase[rawBase];

    if (preferredCode) {
      const canonicalPreferredCode = canonicalByNormalizedCode.get(
        normalizeLanguageTag(preferredCode)
      );
      if (canonicalPreferredCode) {
        return { browserLanguage, languageCode: canonicalPreferredCode };
      }
    }

    const baseMatch = supportedLanguageCodes.find((code) => getLanguageBase(code) === aliasedBase);
    if (baseMatch) {
      return { browserLanguage, languageCode: baseMatch };
    }
  }

  return null;
}
